import { RequestParams } from '../services/http-cache-config.interface';

/**
 * Generate a deterministic cache key from request parameters
 * Uses JSON.stringify with sorted keys to ensure consistent ordering
 *
 * @param params Request parameters
 * @returns Hash-like cache key string
 */
export function generateCacheKey(params: RequestParams): string {
    // Create a normalized object with all parameters
    const normalizedParams = {
        method: params.method.toUpperCase(),
        url: normalizeUrl(params.url),
        queryParams: params.queryParams ? sortObject(params.queryParams) : {},
        body: params.body ? sortObject(params.body) : null,
        headers: params.relevantHeaders ? sortObject(params.relevantHeaders) : {},
    };

    // Stringify with sorted keys for deterministic output
    const jsonString = JSON.stringify(normalizedParams, Object.keys(normalizedParams).sort());

    // Generate a simple hash
    return simpleHash(jsonString);
}

/**
 * Normalize URL by removing trailing slashes and lowercasing
 */
function normalizeUrl(url: string): string {
    return url.toLowerCase().replace(/\/+$/, '');
}

/**
 * Recursively sort object keys for deterministic JSON serialization
 * This ensures that { a: 1, b: 2 } and { b: 2, a: 1 } produce the same cache key
 */
function sortObject(obj: any): any {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => sortObject(item));
    }

    if (typeof obj === 'object') {
        const sortedObj: any = {};
        Object.keys(obj)
            .sort()
            .forEach(key => {
                sortedObj[key] = sortObject(obj[key]);
            });
        return sortedObj;
    }

    return obj;
}

/**
 * Simple hash function for cache key generation
 * Uses FNV-1a algorithm for speed and reasonable distribution
 */
export function simpleHash(str: string): string {
    let hash = 2166136261; // FNV offset basis

    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }

    // Convert to unsigned 32-bit integer and then to base36 string
    return (hash >>> 0).toString(36);
}

/**
 * Extract query params from HttpParams or plain object
 * Useful for services using Angular's HttpParams
 */
export function extractQueryParams(params: any): Record<string, any> {
    if (!params) {
        return {};
    }

    // If it's an HttpParams instance
    if (params.keys && typeof params.keys === 'function') {
        const result: Record<string, any> = {};
        params.keys().forEach((key: string) => {
            result[key] = params.get(key);
        });
        return result;
    }

    // If it's already a plain object
    return params;
}
